<template>
  <modal-wrap :id="id" :name="modalName" :hide-footer="true" size="lg">
    <template #modal-title>
      <h4 class="mb-0">Register inbound delivery</h4>
    </template>
    <form class="mt-1" @submit.prevent="handleSubmit">
      <div class="form-group mb-2">
        <label for="inventory-receipt-description">Description</label>
        <input
          id="inventory-receipt-description"
          v-model="description"
          type="text"
          class="form-control"
          placeholder="Name of the batch, if not provided - date and time of batch will be used by default"
        />
      </div>
      <div v-for="(line, index) in lines" :key="line.id" class="row g-3 align-items-end mb-2">
        <div class="col-12 col-md-9">
          <div class="form-group mb-0">
            <label>Product *</label>
            <div class="inventory-product-select">
              <CustomSelect
                placeholder="Choose product"
                :selected="line.sku"
                :options="productOptions"
                @selected="(value: string) => updateProduct(index, value)"
              />
            </div>
          </div>
        </div>
        <div class="col-8 col-md-2">
          <div class="form-group mb-0">
            <label>Qty *</label>
            <input v-model.number="line.qty" type="number" min="1" class="form-control" @input="normalizeQty(line)" />
          </div>
        </div>
        <div class="col-4 col-md-1 d-flex justify-content-end">
          <button
            v-if="lines.length > 1"
            type="button"
            class="btn btn-grey w-100 d-flex align-items-center justify-content-center"
            @click="removeLine(index)"
          >
            <mdicon name="TrashCanOutline" size="18" />
          </button>
        </div>
      </div>

      <div class="mt-3 d-flex">
        <button type="button" class="btn btn-grey" @click="addLine">
          <mdicon name="plus" size="18" />
        </button>
        <div class="ms-auto d-flex">
          <b-button type="submit" variant="success" class="btn-green" :disabled="isSubmitDisabled">Register</b-button>
        </div>
      </div>
    </form>
  </modal-wrap>
</template>

<script lang="ts">
import type { InboundLineDraft } from '@/types/Models/InventoryInboundReceipt'
import { BButton } from 'bootstrap-vue-next'
import { defineComponent, computed, onMounted, reactive, ref, watch } from 'vue'
import { INVENTORY_ROWS_PER_PAGE } from '@/const/Inventory'
import { REGISTER_INVENTORY_INBOUND_DELIVERY } from '@/const/ModalTypes'
import { registerModals } from '@/services/Modal'
import { useInventoryStore } from '@/store/inventory'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CustomSelect from '@/views/Components/Elements/Form/CustomSelect.vue'

export default defineComponent({
  name: 'RegisterInboundDeliveryModal',
  components: { BButton, ModalWrap, CustomSelect },
  props: {
    id: {
      type: String,
      required: true,
    },
    companyId: {
      type: String,
      default: undefined,
    },
  },
  emits: ['created'],
  setup(props, { emit }) {
    const modalName = REGISTER_INVENTORY_INBOUND_DELIVERY
    const modalStore = useVueNextModalStore()
    const inventoryStore = useInventoryStore()
    const lines = reactive<InboundLineDraft[]>([])
    const description = ref('')

    const productOptions = computed(() =>
      inventoryStore.productsList.map((product) => ({
        handle: product.sku,
        label: `${product.sku} · ${product.name}`,
      })),
    )

    const isSubmitDisabled = computed(() => {
      if (!props.companyId) return true
      if (!lines.length) return true
      return lines.some((line) => !line.sku || !line.qty || line.qty <= 0)
    })

    const addLine = () => {
      lines.push({
        id: `line-${Date.now()}-${lines.length}`,
        sku: '',
        qty: null,
      })
    }

    const removeLine = (index: number) => {
      if (lines.length === 1) return
      lines.splice(index, 1)
    }

    const updateProduct = (index: number, value: string) => {
      const line = lines[index]
      if (!line) return
      line.sku = value
    }

    const normalizeQty = (line: InboundLineDraft) => {
      if (line.qty === null || Number.isNaN(line.qty)) {
        line.qty = null
        return
      }
      line.qty = Math.max(1, Math.floor(line.qty))
    }

    const reset = () => {
      lines.splice(0, lines.length)
      description.value = ''
      addLine()
    }

    const close = () => {
      modalStore.closeModal(modalName)
      reset()
    }

    const handleSubmit = async () => {
      if (isSubmitDisabled.value) return
      await inventoryStore.createInboundReceipt(props.companyId ?? '', {
        description: description.value.trim(),
        lines: lines
          .filter((line) => line.sku && line.qty && line.qty > 0)
          .map((line) => ({
            sku: line.sku,
            qty: line.qty as number,
          })),
      })
      emit('created')
      close()
    }

    onMounted(() => {
      registerModals(modalName)
      reset()
    })

    watch(
      () => modalStore.isModalVisible(modalName),
      (isOpen) => {
        if (!isOpen || !props.companyId) return
        if (inventoryStore.productsList.length === 0) {
          inventoryStore.fetchProducts(props.companyId, { page: 1, rowsPerPage: INVENTORY_ROWS_PER_PAGE })
        }
      },
    )

    return {
      modalName,
      lines,
      description,
      productOptions,
      isSubmitDisabled,
      addLine,
      removeLine,
      updateProduct,
      normalizeQty,
      handleSubmit,
    }
  },
})
</script>

<style scoped>
.inventory-product-select {
  width: 100%;
}

.inventory-product-select :deep(.custom-selectbox) {
  width: 100%;
}

.inventory-product-select :deep(.vs__dropdown-toggle) {
  width: 100%;
}

</style>
